// src/components/feature-configurator/utils/chat.ts

import {
  LLMResponse,
  AskError,
  AssistantItem,
  AnswerPayload,
  PatchPayload,
  LegacyAnswer,
  AnswerBlock,
  ApiEnvelope,
  PatchEdit,
  GraphDefinition,
  EventTrendRow,
} from "./types";
import { dedupe } from "./formatter";

export function isGraphDefinitionLike(
  content: unknown
): content is GraphDefinition {
  if (!content || typeof content !== "object") return false;
  const g = content as any;

  return (
    g.kind === "timeseries" &&
    Array.isArray(g.series) &&
    g.series.length > 0 &&
    Array.isArray(g.series[0]?.points)
  );
}

export function graphDefToEventRows(graph: GraphDefinition): EventTrendRow[] {
  const primary =
    graph.series.find(s => (s as any).isPrimary) || graph.series[0];

  if (!primary || !Array.isArray(primary.points)) return [];

  return primary.points.map(p => ({
    event_date: String(p.x),
    event_count:
      typeof p.y === "number"
        ? p.y
        : Number.isFinite(Number(p.y))
        ? Number(p.y)
        : 0,
  }));
}

export function findPreviewJsonInBlocks(
  blocks?: AnswerBlock[]
): any | undefined {
  const code = (blocks || []).find(
    b => b.type === "code" && typeof b.content === "string"
  )?.content as string | undefined;
  if (!code) return undefined;
  try {
    return JSON.parse(code);
  } catch {
    return undefined;
  }
}

export function hydratePatchForRender(
  patch: AssistantItem["patch"] | undefined,
  blocks?: AnswerBlock[]
): {
  hasDefault: boolean;
  defaultVal?: string | number | boolean;
  edits: PatchEdit[];
  comment?: string;
  apply: boolean;
} {
  if (!patch) return { hasDefault: false, edits: [], apply: false };

  const inlineHasDefault = Object.prototype.hasOwnProperty.call(
    patch as any,
    "default"
  );
  const inlineDefault = inlineHasDefault ? (patch as any).default : undefined;
  const inlineEdits = Array.isArray(patch.edits) ? patch.edits : [];
  const inlineComment = patch.comment;

  let pObj: any | undefined;
  if (patch.previewCode) {
    try {
      pObj = JSON.parse(patch.previewCode);
    } catch {}
  }
  if (!pObj) {
    pObj = findPreviewJsonInBlocks(blocks);
  }

  const previewHasDefault =
    pObj && Object.prototype.hasOwnProperty.call(pObj, "default");
  const previewDefault = previewHasDefault ? pObj.default : undefined;
  const previewEdits = Array.isArray(pObj?.edits)
    ? (pObj.edits as PatchEdit[])
    : [];
  const previewComment = pObj?.comment as string | undefined;

  const defaultVal = inlineHasDefault
    ? inlineDefault
    : previewHasDefault
    ? previewDefault
    : undefined;

  const edits = inlineEdits.length ? inlineEdits : previewEdits;
  const comment =
    (inlineComment && inlineComment.length ? inlineComment : previewComment) ||
    "";

  const previewApplyRaw = pObj?.apply;
  const previewApply =
    typeof previewApplyRaw === "boolean"
      ? previewApplyRaw
      : typeof previewApplyRaw === "string"
      ? previewApplyRaw.trim().toLowerCase() === "true"
      : false;

  const apply = patch.apply === true || previewApply === true;

  return {
    hasDefault: typeof defaultVal !== "undefined",
    defaultVal,
    edits,
    comment,
    apply,
  };
}

function coercePatchValue(value: unknown): string | number | boolean {
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }

  // Fallback: stringify anything else (null, objects, etc.)
  return value == null ? "" : String(value);
}

export function parsePatchFromPreview(previewCode?: string): {
  hasDefault: boolean;
  defaultVal?: string | number | boolean;
  edits: PatchEdit[];
  comment?: string;
} {
  if (!previewCode) return { hasDefault: false, edits: [] };

  try {
    const obj = JSON.parse(previewCode);
    const hasDefault = Object.prototype.hasOwnProperty.call(obj, "default");
    const defaultVal = hasDefault ? (obj.default as any) : undefined;
    const edits = Array.isArray(obj.edits) ? (obj.edits as PatchEdit[]) : [];
    return { hasDefault, defaultVal, edits, comment: obj.comment };
  } catch {
    return { hasDefault: false, edits: [] };
  }
}

export function coerceEnvelope(
  payload: unknown
): LLMResponse | AskError | undefined {
  if (!payload) return undefined;

  const maybe = payload as ApiEnvelope;
  if (typeof maybe === "object" && maybe !== null && "data" in maybe) {
    return maybe.data as any;
  }

  return payload as any;
}

export function normalizeToChatItem(
  resp: LLMResponse | AskError
): AssistantItem {
  if ((resp as AskError)?.type === "error") {
    const e = resp as AskError;
    return {
      role: "assistant",
      isError: true,
      prose: e.message || "Request failed.",
      raw: e.raw,
    };
  }

  if ((resp as AnswerPayload).type === "answer" && "prose" in (resp as any)) {
    const a = resp as AnswerPayload;

    const graphBlocks: AnswerBlock[] =
      Array.isArray(a.graphs) && a.graphs.length
        ? a.graphs.map(g => ({
            type: "raw" as const,
            label: g.title || g.id,
            content: g,
          }))
        : [];

    const mergedBlocks = [...(a.blocks || []), ...graphBlocks];

    // NEW: lift immediateRcPatch into our generic patch format
    let patch: AssistantItem["patch"] | undefined;

    if (a.immediateRcPatch) {
      const p = a.immediateRcPatch;

      const edits: PatchEdit[] =
        p.set && typeof p.set === "object"
          ? Object.entries(p.set).map(([condition, value]) => ({
              condition,
              value: coercePatchValue(value),
            }))
          : [];

      const previewObj: any = {
        parameter: p.parameter,
        edits,
        comment: p.comment ?? "",
        docId: p.docId,
        applyImmediate: p.applyImmediate,
        default: p.default,
        set: p.set,
      };

      patch = {
        parameter: p.parameter,
        edits,
        comment: p.comment ?? "",
        apply: p.applyImmediate,
        previewHeader: `Immediate RC: ${p.parameter}`,
        previewCode: JSON.stringify(previewObj, null, 2),
        default: p.default,
      };
    }

    return {
      role: "assistant",
      header: a.header,
      prose: a.prose,
      chips: dedupe(a.chips || []),
      blocks: mergedBlocks,
      matchedParameter: (a as any).matchedParameter,
      severity: a.severity,
      patch,
      immediateRcPatch: a.immediateRcPatch,
    };
  }

  if ((resp as PatchPayload).type === "patch" && "parameter" in (resp as any)) {
    const p = resp as PatchPayload;

    const header = p.preview?.header || `Proposed patch for "${p.parameter}"`;
    const prose = p.comment;

    const fromPreview = parsePatchFromPreview(p.preview?.code);
    const hasDefaultFromPreview = fromPreview.hasDefault;
    const defaultFromPreview = fromPreview.defaultVal;

    const hasDefaultInline = Object.prototype.hasOwnProperty.call(p, "default");
    const defaultInline = (p as any).default as any;

    const hydratedDefault = hasDefaultInline
      ? defaultInline
      : hasDefaultFromPreview
      ? defaultFromPreview
      : undefined;

    const inlineEdits = Array.isArray(p.edits) ? p.edits : [];
    const previewEdits = Array.isArray(fromPreview.edits)
      ? fromPreview.edits
      : [];
    const hydratedEdits = inlineEdits.length ? inlineEdits : previewEdits;

    return {
      role: "assistant",
      header,
      prose,
      blocks: p.preview?.code
        ? [
            {
              type: "code",
              label: "Patch",
              language: "json",
              content: p.preview.code,
            },
          ]
        : [],
      matchedParameter: p.parameter,
      severity: "info",
      patch: {
        parameter: p.parameter,
        edits: hydratedEdits,
        comment: p.comment || fromPreview.comment || "",
        apply: p.apply,
        previewHeader: p.preview?.header,
        previewCode: p.preview?.code,
        default: hydratedDefault,
      },
    };
  }

  const legacy = resp as LegacyAnswer;
  if (legacy.type === "answer") {
    const blocks: AnswerBlock[] = [];
    if (legacy.uiHints?.codeExample) {
      blocks.push({
        type: "code",
        label: "Example",
        language: "json",
        content: legacy.uiHints.codeExample,
      });
    }
    return {
      role: "assistant",
      header: legacy.uiHints?.title,
      prose: legacy.text,
      chips: dedupe(legacy.uiHints?.chips || []),
      blocks,
      matchedParameter: legacy.matchedParameter,
      severity: legacy.uiHints?.severity,
      raw: legacy.raw,
    };
  }
  return {
    role: "assistant",
    prose: "Unsupported response shape.",
    isError: true,
    raw: resp,
  };
}

export function describeImplication(
  parameter: string,
  edits: PatchEdit[] | undefined,
  globalDefault?: string | number | boolean
): string {
  const isForce =
    /minimal/i.test(parameter) &&
    /required/i.test(parameter) &&
    /version/i.test(parameter);

  if (typeof globalDefault !== "undefined") {
    const target = globalDefault;
    if (isForce) {
      return `This will enforce the minimum app version globally to "${target}". Users below this version may be blocked until they update, depending on your client logic.`;
    }
    return `This will update "${parameter}" globally to "${target}". Clients pick this up after their next Remote Config fetch.`;
  }

  const list = Array.isArray(edits) ? edits : [];
  const conds = list.map(e => e.condition);
  const uniqueConds = Array.from(new Set(conds));
  const many =
    uniqueConds.length > 6
      ? `${uniqueConds.length} conditions`
      : uniqueConds.join(", ");

  if (isForce) {
    const sample = list.find(Boolean)?.value ?? "(new version)";
    return `This will enforce the minimum app version to "${sample}" for ${many}. Users below this version may be blocked until they update, depending on your client logic.`;
  }
  return `This change updates "${parameter}" for ${many}. It will take effect after clients fetch Remote Config (usually on next app start or your fetch interval).`;
}
